import { useTheme } from '@material-ui/core/styles';
import { numberWithCommas as formatNumber } from '@roc/feature-utils';
import { StatusChip, StatusType } from '@roc/ui';
import clsx from 'clsx';
import { observer } from 'mobx-react';
import { useStatusDashboardStore } from '../hooks/useStatusDashboardStore';
import { getColumnFilters, RecipientSortColumn } from '../stores/statusDashboardStore';
import { RECIPIENT_OPENED_OPTIONS, RECIPIENT_STATUS_OPTIONS } from '../utils/statusDashboardConstants';
import { DashboardCard } from './dashboardCard';
import { GridFooter } from './gridFooter';
import { ColumnFilter } from './columnFilter';
import { ColumnSelectFilter } from './columnSelectFilter';
import { SortableHeader } from './sortableHeader';
import { MetricBar } from './metricBar';
import { DASHBOARD_COLORS, useStatusDashboardStyles } from './statusDashboardStyles';

// The sender column is shown only when the campaign can have more than one sender
const getRecipientColumns = (showSenders: boolean) =>
  `1.3fr 1.5fr ${showSenders ? '1.1fr ' : ''}1fr .8fr .9fr 1.4fr`;

const CampaignSummary = observer(() => {
  const classes = useStatusDashboardStyles();
  const theme = useTheme();
  const { statusDashboardStore: store } = useStatusDashboardStore();
  const campaign = store.selectedCampaign;
  const funnel = store.campaignFunnel;
  if (!campaign) return null;
  const stageColors = [theme.palette.primary.main, ...DASHBOARD_COLORS.chart];

  return (
    <DashboardCard>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <h2 className={classes.cardTitle} style={{ fontSize: 20 }}>{campaign.name}</h2>
        </div>
        <span className={classes.muted}>
          {store.channelData.label} · {campaign.date} · {campaign.segment} · {formatNumber(campaign.requested)} requested,{' '}
          {formatNumber(campaign.skipped)} skipped
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${funnel.length}, minmax(0,1fr))`, gap: 8 }}>
        {funnel.map((stage, i) => (
          <div key={stage.label} className={classes.tile}>
            <span className={classes.muted}>{stage.label}</span>
            <span className={classes.tileValue}>{formatNumber(stage.count)}</span>
            <MetricBar value={stage.pct} height={6} color={stageColors[i % stageColors.length]} />
            <span className={classes.mutedSmall}>{stage.pct.toFixed(1)}% of requested</span>
          </div>
        ))}
      </div>
    </DashboardCard>
  );
});

const FailureReasons = observer(() => {
  const classes = useStatusDashboardStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();

  return (
    <DashboardCard
      title={store.channelData.failTitle}
      right={`${formatNumber(store.campaignFailedTotal)} of ${formatNumber(store.selectedCampaign?.sent ?? 0)} sent`}
    >
      {store.campaignFailures.length === 0 && <span className={classes.muted}>No bounced or dropped emails</span>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0,1fr))', gap: 8 }}>
        {store.campaignFailures.map(f => (
          <div key={f.reason} className={classes.tile}>
            <span className={classes.tileValue} style={{ fontSize: 22 }}>{formatNumber(f.count)}</span>
            <MetricBar value={f.pct} height={4} color={DASHBOARD_COLORS.destructive} />
            <span className={classes.muted}>{f.reason}</span>
          </div>
        ))}
      </div>
    </DashboardCard>
  );
});

const RecipientHeader = observer(({ column, label }: { column: RecipientSortColumn; label: string }) => {
  const { statusDashboardStore: store } = useStatusDashboardStore();
  return (
    <SortableHeader
      grid={store.recipientsGrid}
      column={column}
      label={label}
      onSort={() => store.setRecipientSort(column)}
      alignLeft
    />
  );
});

const Recipients = observer(() => {
  const classes = useStatusDashboardStyles();
  const { statusDashboardStore: store } = useStatusDashboardStore();
  const filters = getColumnFilters(store.recipientsGrid);
  const { showSenders } = store;
  const recipientColumns = getRecipientColumns(showSenders);
  const campaign = store.selectedCampaign;
  const senderOptions = (campaign?.senders ?? []).map(sender => ({ value: sender, label: store.senderName(sender) }));
  const audienceOptions = (campaign?.audiences ?? []).map(audience => ({ value: audience, label: audience }));

  return (
    <DashboardCard title="Recipients" right="Select a recipient to see their full send history">
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <div className={classes.tableHead} style={{ gridTemplateColumns: recipientColumns }}>
          <RecipientHeader column="name" label="Recipient" />
          <RecipientHeader column="address" label={store.channelData.address} />
          {showSenders && <RecipientHeader column="sender" label={store.senderLabel} />}
          <RecipientHeader column="audience" label="Audience" />
          <RecipientHeader column="opened" label="Opened" />
          <RecipientHeader column="status" label="Status" />
          <RecipientHeader column="skipReason" label="Skip reason" />
        </div>
        <div className={classes.filterRow} style={{ gridTemplateColumns: recipientColumns }}>
          <ColumnFilter value={filters.name ?? ''} onFilter={value => store.setRecipientFilter('name', value)} />
          <ColumnFilter value={filters.address ?? ''} onFilter={value => store.setRecipientFilter('address', value)} />
          {showSenders && (
            <ColumnSelectFilter
              value={filters.sender ?? ''}
              options={senderOptions}
              onFilter={value => store.setRecipientFilter('sender', value)}
              searchable
            />
          )}
          <ColumnSelectFilter
            value={filters.audience ?? ''}
            options={audienceOptions}
            onFilter={value => store.setRecipientFilter('audience', value)}
          />
          <ColumnSelectFilter
            value={filters.opened ?? ''}
            options={RECIPIENT_OPENED_OPTIONS}
            onFilter={value => store.setRecipientFilter('opened', value)}
          />
          <ColumnSelectFilter
            value={filters.status ?? ''}
            options={RECIPIENT_STATUS_OPTIONS}
            onFilter={value => store.setRecipientFilter('status', value)}
          />
          <ColumnSelectFilter
            value={filters.skipReason ?? ''}
            options={store.skipReasonOptions}
            onFilter={value => store.setRecipientFilter('skipReason', value)}
          />
        </div>
        {store.recipients.map(r => (
          <div
            key={r.key}
            className={clsx(classes.tableRow, classes.clickableRow)}
            style={{ gridTemplateColumns: recipientColumns }}
            onClick={() => store.openRecipient(r)}
          >
            <span className={classes.bold}>{r.name}</span>
            <span className={clsx(classes.ellipsis)} style={{ color: DASHBOARD_COLORS.muted }}>{r.address}</span>
            {showSenders && (
              <span className={classes.ellipsis} title={r.sender}>
                {store.senderName(r.sender)}
              </span>
            )}
            <span>{r.segment}</span>
            <span className={classes.number}>{r.openCount === null ? '—' : formatNumber(r.openCount)}</span>
            <span>
              <StatusChip statusType={StatusType.BULK_SEND} label={r.status} variant="filled" size="small" />
            </span>
            <span style={{ color: DASHBOARD_COLORS.muted }}>{r.skipReason}</span>
          </div>
        ))}
        <GridFooter
          grid={store.recipientsGrid}
          emptyText={Object.keys(filters).length ? 'No recipients match the filters' : 'No recipients in this range'}
        />
      </div>
    </DashboardCard>
  );
});

export const CampaignDetailTab = () => {
  const classes = useStatusDashboardStyles();
  return (
    <div className={classes.stack}>
      <CampaignSummary />
      <FailureReasons />
      <Recipients />
    </div>
  );
};
